/**
 * ManageCategoriesPanel.tsx — the "Gestisci categorie" drawer, opened from the
 * "Categorie & voci" tab of the Audit screen (that tab's own content is left
 * untouched, per Omar's 2026-07-28 decision).
 *
 * Two jobs, two sections:
 *  - **Struttura** — create a sub-category under a main one, rename it, move it
 *    to a different main, delete it. Main categories are the legal SOA taxonomy
 *    (OG1, OS23…) and are never created here; the backend refuses it too.
 *  - **Sposta voci** — move line items between categories, including items the
 *    analysis already filed (the AI's guess can be wrong, not only absent).
 *
 * Every failure is shown verbatim from the server. A delete blocked because the
 * category carries a make/buy strategy must SAY so — silently doing nothing is
 * the failure mode this panel exists to avoid.
 */
import { useMemo, useState } from "react";

import { I } from "../../components/icons";
import { apiErrorMessage } from "../../lib/api";
import {
  type AuditCategory,
  type AuditLineItem,
  useBulkCategorizeLineItems,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../../lib/tenderApi";
import { fmtEur } from "./helpers";
import { ItemBillPage, type ItemPagesView } from "./ItemBillPage";

const UNFILED = "__unfiled__";

interface Props {
  projectId: string;
  categories: AuditCategory[];
  lineItems: AuditLineItem[];
  onClose: () => void;
  /** G3a — the bill page of each item; absent outside the Audit page (nothing is shown). */
  itemPages?: ItemPagesView;
}

function label(c: { code: string | null; name: string }): string {
  return `${c.code ?? ""} — ${c.name}`.replace(/^ — /, "");
}

export function ManageCategoriesPanel({
  projectId,
  categories,
  lineItems,
  onClose,
  itemPages,
}: Props): JSX.Element {
  const [tab, setTab] = useState<"struttura" | "voci">("struttura");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const create = useCreateCategory(projectId);
  const updateCat = useUpdateCategory(projectId);
  const removeCat = useDeleteCategory(projectId);
  const move = useBulkCategorizeLineItems(projectId);

  // Only real main categories can be a parent — never the "senza categoria"
  // bucket, which is a rendering device rather than a place to file work.
  const mains = useMemo(() => categories.filter((c) => !c.isUncategorized), [categories]);

  const busy = create.isPending || updateCat.isPending || removeCat.isPending || move.isPending;

  function run(p: Promise<unknown>, success: string): void {
    setError(null);
    setNotice(null);
    p.then(() => setNotice(success)).catch((e: unknown) => setError(apiErrorMessage(e)));
  }

  // ── create ────────────────────────────────────────────────────────────────
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [newParent, setNewParent] = useState("");

  function onCreate(): void {
    const name = newName.trim();
    if (!name || !newParent) return;
    run(
      create.mutateAsync({ name, code: newCode.trim() || null, parentId: newParent }).then(() => {
        setNewName("");
        setNewCode("");
      }),
      `Sotto-categoria "${name}" creata.`,
    );
  }

  // ── rename ────────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");

  function startEdit(c: { id: string; name: string; code: string | null }): void {
    setEditing(c.id);
    setEditName(c.name);
    setEditCode(c.code ?? "");
    setError(null);
    setNotice(null);
  }

  function saveEdit(categoryId: string): void {
    const name = editName.trim();
    if (!name) {
      setError("Il nome non può essere vuoto.");
      return;
    }
    run(
      updateCat
        .mutateAsync({ categoryId, name, code: editCode.trim() || null })
        .then(() => setEditing(null)),
      "Categoria aggiornata.",
    );
  }

  // ── delete ────────────────────────────────────────────────────────────────
  const [confirming, setConfirming] = useState<string | null>(null);

  function onDelete(categoryId: string, name: string): void {
    run(
      removeCat.mutateAsync({ categoryId }).then((r) => {
        setConfirming(null);
        return r;
      }),
      `"${name}" eliminata.`,
    );
  }

  // ── move line items ───────────────────────────────────────────────────────
  const [source, setSource] = useState<string>(UNFILED);
  const [target, setTarget] = useState<string>("");
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const sourceItems = useMemo(
    () =>
      lineItems.filter((i) =>
        source === UNFILED ? i.categoryId == null : i.categoryId === source,
      ),
    [lineItems, source],
  );

  // Everything assignable, main and sub alike, minus wherever we're moving FROM.
  const targets = useMemo(() => {
    const out: { id: string; text: string }[] = [];
    for (const c of mains) {
      out.push({ id: c.id, text: label(c) });
      for (const s of c.subs) out.push({ id: s.id, text: `    ${label(s)}` });
    }
    return out.filter((o) => o.id !== source);
  }, [mains, source]);

  function togglePick(id: string): void {
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  function onMove(): void {
    if (picked.size === 0 || !target) return;
    const count = picked.size;
    run(
      move.mutateAsync({ ids: [...picked], categoryId: target }).then((r) => {
        setPicked(new Set());
        return r;
      }),
      `${count} voce/i spostate.`,
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Gestisci categorie"
      style={{
        // Nested inside the table card, directly under the tab bar — so NOT a
        // card of its own. Tinted and rule-bounded instead, so it reads as an
        // editing surface laid over the table rather than a separate panel.
        background: "var(--c-surface-2, rgba(0,0,0,0.02))",
        borderBottom: "2px solid var(--c-line)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          borderBottom: "1px solid var(--c-line)",
        }}
      >
        <I.Settings size={16} />
        <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>Gestisci categorie</div>
        <button className="btn btn-sm" onClick={onClose} aria-label="Chiudi">
          <I.X size={14} />
        </button>
      </div>

      <div style={{ display: "flex", borderBottom: "1px solid var(--c-line)" }}>
        {(
          [
            ["struttura", "Struttura"],
            ["voci", "Sposta voci"],
          ] as const
        ).map(([key, text]) => (
          <button
            key={key}
            onClick={() => {
              setTab(key);
              setError(null);
              setNotice(null);
            }}
            style={{
              padding: "9px 16px",
              fontSize: 12.5,
              fontWeight: tab === key ? 600 : 400,
              background: "none",
              border: "none",
              borderBottom: tab === key ? "2px solid var(--c-ink)" : "2px solid transparent",
              cursor: "pointer",
              color: tab === key ? "var(--c-ink)" : "var(--c-ink-3)",
            }}
          >
            {text}
          </button>
        ))}
      </div>

      {(error || notice) && (
        <div
          role={error ? "alert" : "status"}
          style={{
            padding: "9px 16px",
            fontSize: 12,
            background: error ? "var(--c-err-soft, #fdeeee)" : "var(--c-ok-soft, #eef7f0)",
            color: error ? "var(--c-err)" : "var(--c-ok, #1b6b3a)",
            borderBottom: "1px solid var(--c-line)",
          }}
        >
          {error ?? notice}
        </div>
      )}

      {tab === "struttura" ? (
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginBottom: 10 }}>
            Le categorie principali seguono la classificazione SOA e non si creano a mano. Qui
            aggiungi <strong>sotto-categorie</strong> sotto una principale esistente.
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: 16,
            }}
          >
            <select
              className="input"
              aria-label="Categoria principale"
              value={newParent}
              onChange={(e) => setNewParent(e.target.value)}
              style={{ height: 30, fontSize: 12, minWidth: 190 }}
            >
              <option value="">Categoria principale…</option>
              {mains.map((c) => (
                <option key={c.id} value={c.id}>
                  {label(c)}
                </option>
              ))}
            </select>
            <input
              className="input"
              aria-label="Nome sotto-categoria"
              placeholder="Nome sotto-categoria"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              style={{ height: 30, fontSize: 12, minWidth: 180 }}
            />
            <input
              className="input"
              aria-label="Codice (facoltativo)"
              placeholder="Codice (facolt.)"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              style={{ height: 30, fontSize: 12, width: 130 }}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={onCreate}
              disabled={busy || !newName.trim() || !newParent}
            >
              <I.Plus size={13} /> Crea
            </button>
          </div>

          <table className="tbl" style={{ fontSize: 12.5 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Categoria</th>
                <th style={{ textAlign: "right", width: 130 }}>Costo</th>
                <th style={{ width: 240 }} />
              </tr>
            </thead>
            <tbody>
              {mains.map((c) => (
                <FragmentRows
                  key={c.id}
                  main={c}
                  mains={mains}
                  busy={busy}
                  editing={editing}
                  editName={editName}
                  editCode={editCode}
                  setEditName={setEditName}
                  setEditCode={setEditCode}
                  startEdit={startEdit}
                  cancelEdit={() => setEditing(null)}
                  saveEdit={saveEdit}
                  confirming={confirming}
                  setConfirming={setConfirming}
                  onDelete={onDelete}
                  onReparent={(categoryId, parentId) =>
                    run(updateCat.mutateAsync({ categoryId, parentId }), "Categoria spostata.")
                  }
                />
              ))}
              {mains.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    style={{ padding: 18, textAlign: "center", color: "var(--c-ink-3)" }}
                  >
                    Nessuna categoria: esegui prima l&apos;analisi del progetto.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginBottom: 10 }}>
            Sposta le voci fra le categorie — anche quelle già assegnate dall&apos;analisi.
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
            <select
              className="input"
              aria-label="Da"
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                setPicked(new Set());
              }}
              style={{ height: 30, fontSize: 12, minWidth: 220 }}
            >
              <option value={UNFILED}>Senza categoria</option>
              {mains.map((c) => (
                <optgroup key={c.id} label={label(c)}>
                  <option value={c.id}>{label(c)}</option>
                  {c.subs.map((s) => (
                    <option key={s.id} value={s.id}>
                      {label(s)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <I.Arrow size={14} />
            <select
              className="input"
              aria-label="A"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              style={{ height: 30, fontSize: 12, minWidth: 220 }}
            >
              <option value="">Sposta a…</option>
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.text}
                </option>
              ))}
            </select>
            <button
              className="btn btn-primary btn-sm"
              onClick={onMove}
              disabled={busy || picked.size === 0 || !target}
            >
              Sposta {picked.size > 0 ? `(${picked.size})` : ""}
            </button>
          </div>

          <table className="tbl" style={{ fontSize: 12.5 }}>
            <thead>
              <tr>
                <th style={{ width: 34 }}>
                  <input
                    type="checkbox"
                    aria-label="Seleziona tutte"
                    checked={sourceItems.length > 0 && picked.size === sourceItems.length}
                    onChange={(e) =>
                      setPicked(
                        e.target.checked ? new Set(sourceItems.map((i) => i.id)) : new Set(),
                      )
                    }
                  />
                </th>
                <th style={{ textAlign: "left", width: 90 }}>ID</th>
                <th style={{ textAlign: "left" }}>Descrizione</th>
                <th style={{ textAlign: "right", width: 120 }}>Totale</th>
              </tr>
            </thead>
            <tbody>
              {sourceItems.slice(0, 200).map((i) => (
                <tr key={i.id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Seleziona ${i.code ?? i.id}`}
                      checked={picked.has(i.id)}
                      onChange={() => togglePick(i.id)}
                    />
                  </td>
                  <td style={{ fontFamily: "var(--font-mono, monospace)" }}>{i.code ?? "—"}</td>
                  <td
                    style={{
                      maxWidth: 420,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {itemPages ? (
                      // G3a: the description keeps its ellipsis and the bill page stays in view.
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                          {i.description}
                        </span>
                        <span style={{ flex: "none", whiteSpace: "normal" }}>
                          <ItemBillPage itemId={i.id} pages={itemPages} />
                        </span>
                      </div>
                    ) : (
                      i.description
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>{i.total == null ? "—" : fmtEur(i.total)}</td>
                </tr>
              ))}
              {sourceItems.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    style={{ padding: 18, textAlign: "center", color: "var(--c-ink-3)" }}
                  >
                    Nessuna voce in questa categoria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {sourceItems.length > 200 && (
            <div style={{ padding: "8px 2px", fontSize: 11.5, color: "var(--c-ink-3)" }}>
              Mostrate le prime 200 di {sourceItems.length.toLocaleString("it-IT")} voci.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface RowsProps {
  main: AuditCategory;
  mains: AuditCategory[];
  busy: boolean;
  editing: string | null;
  editName: string;
  editCode: string;
  setEditName: (v: string) => void;
  setEditCode: (v: string) => void;
  startEdit: (c: { id: string; name: string; code: string | null }) => void;
  cancelEdit: () => void;
  saveEdit: (id: string) => void;
  confirming: string | null;
  setConfirming: (id: string | null) => void;
  onDelete: (id: string, name: string) => void;
  onReparent: (categoryId: string, parentId: string) => void;
}

/** A main category followed by its sub-categories. */
function FragmentRows({
  main,
  mains,
  busy,
  editing,
  editName,
  editCode,
  setEditName,
  setEditCode,
  startEdit,
  cancelEdit,
  saveEdit,
  confirming,
  setConfirming,
  onDelete,
  onReparent,
}: RowsProps): JSX.Element {
  const editor = (id: string): JSX.Element => (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <input
        className="input"
        aria-label="Nome"
        value={editName}
        onChange={(e) => setEditName(e.target.value)}
        style={{ height: 26, fontSize: 12, flex: 1 }}
      />
      <input
        className="input"
        aria-label="Codice"
        value={editCode}
        onChange={(e) => setEditCode(e.target.value)}
        style={{ height: 26, fontSize: 12, width: 100 }}
      />
      <button className="btn btn-primary btn-sm" onClick={() => saveEdit(id)} disabled={busy}>
        <I.Check size={12} />
      </button>
      <button className="btn btn-sm" onClick={cancelEdit} disabled={busy}>
        <I.X size={12} />
      </button>
    </div>
  );

  return (
    <>
      <tr>
        <td style={{ fontWeight: 600 }}>{editing === main.id ? editor(main.id) : label(main)}</td>
        <td style={{ textAlign: "right" }}>{main.cost != null ? fmtEur(main.cost) : "—"}</td>
        <td style={{ textAlign: "right" }}>
          {editing !== main.id && (
            <button className="btn btn-sm" onClick={() => startEdit(main)} disabled={busy}>
              <I.Edit size={12} /> Rinomina
            </button>
          )}
        </td>
      </tr>
      {main.subs.map((s) => (
        <tr key={s.id}>
          <td style={{ paddingLeft: 26 }}>
            {editing === s.id ? editor(s.id) : <span>↳ {label(s)}</span>}
          </td>
          <td style={{ textAlign: "right" }}>{s.cost != null ? fmtEur(s.cost) : "—"}</td>
          <td style={{ textAlign: "right" }}>
            {editing === s.id ? null : confirming === s.id ? (
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>Eliminare?</span>
                <button
                  className="btn btn-sm"
                  onClick={() => onDelete(s.id, s.name)}
                  disabled={busy}
                  style={{ color: "var(--c-err)" }}
                >
                  Sì, elimina
                </button>
                <button className="btn btn-sm" onClick={() => setConfirming(null)} disabled={busy}>
                  Annulla
                </button>
              </span>
            ) : (
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <select
                  className="input"
                  aria-label={`Sposta ${s.name} sotto`}
                  value={main.id}
                  onChange={(e) => onReparent(s.id, e.target.value)}
                  disabled={busy}
                  style={{ height: 26, fontSize: 11.5, maxWidth: 260 }}
                >
                  {mains.map((m) => (
                    <option key={m.id} value={m.id}>
                      {/* `label()`, not `m.code` — every other control in this
                          panel shows "code — name", and this is the one that
                          MOVES MONEY. Category codes are deliberately not
                          unique (a document really does print `Cat 1` for two
                          unrelated sections), so code-only rendering made 14 of
                          AOSTA's 46 options byte-identical. This file already
                          refuses a duplicate sub-category NAME with a 409 for
                          exactly this reason: money filed into "the wrong one"
                          is undetectable afterwards. */}
                      {label(m)}
                    </option>
                  ))}
                </select>
                <button className="btn btn-sm" onClick={() => startEdit(s)} disabled={busy}>
                  <I.Edit size={12} />
                </button>
                <button className="btn btn-sm" onClick={() => setConfirming(s.id)} disabled={busy}>
                  <I.Trash size={12} />
                </button>
              </span>
            )}
          </td>
        </tr>
      ))}
    </>
  );
}
